import Link from 'next/link'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'
import { IR_TOP_ROUTE } from '@/utils/routes'

import { CalenderCard } from './CalenderCard'
import { MonthlySalesCard, MonthlySalesCardProps } from './MonthlySalesCard'
import { RecentNewsList } from './RecentNewsList'
import { StockPriceInfoCard } from './StockPriceInfoCard'
import { Button } from '@/components/elements/Button'
import { LinkCard } from '@/components/elements/LinkCard'
import { LinkCardList } from '@/components/elements/LinkCardList'
import { Spacer } from '@/components/elements/Spacer'
import { ImageTitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'
import { Layout } from '@/components/layouts/Ir'
import { DownloadZipButton } from '@/components/layouts/Ir/Mpocket/DownloadZipButton'
import { CmsLinkCard } from '@/components/pages/Ir/CmsLinkCard'

import { useMpocketScript } from '@/hooks/useMpocketScript'
import type { IrCalendar } from '@/types/irCalendar'


type IrFinancialEvent = {
  date: string
  title: string
  held: string
}

export type IrPageProps = {
  irFinancialEvent: IrFinancialEvent
  monthlySales: MonthlySalesCardProps
  irCalendar: IrCalendar[]
}

const irTopMessageLink = IR_TOP_ROUTE.subdirectories[0]
const irFinancialEventLink = IR_TOP_ROUTE.subdirectories[1]
const irInvestorLink = IR_TOP_ROUTE.subdirectories[2]
const irNewsLink = IR_TOP_ROUTE.subdirectories[3].path

export const IrPage = ({ irFinancialEvent, monthlySales, irCalendar }: IrPageProps) => {
  useMpocketScript('//irpocket.com/141A/irpocket/config-utils-main.js')
  // useMpocketScript('//irpocket.com/5882/irpocket/config-utils-main.js')
  
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '株主・投資家向け情報', path: '' },
  ]

  return (
    <>
      <Head
        title="株主・投資家向け情報"
        description="トライアルホールディングスの株主・投資家の皆様に向けた情報を掲載しています。"
        ogp={{ url: 'ir/' }}
      />
      <ImageTitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        imgUrl="/images/top-ir.webp"
        breadCrumbs={breadCrumbs}
        isRequiredBackground={false}
      >
        <Layout>
          <LinkCardList>
            <LinkCard
              title={irTopMessageLink.name}
              link={irTopMessageLink.path}
              imgPath={irTopMessageLink.imagePath}
              isExternalLink={irTopMessageLink.isExternalLink}
            />
            
              <CmsLinkCard
                title={irFinancialEvent.title}
                subtitle={`
                ${formatDate(
                  irFinancialEvent.date,
                  'yyyy年MM月dd日'
                )} 
                ${irFinancialEvent.held}
                `}
                link={irFinancialEventLink.path}
                imgPath={irFinancialEventLink.imagePath}
              />
          

            <LinkCard
              title={irInvestorLink.name}
              link={irInvestorLink.path}
              imgPath={irInvestorLink.imagePath}
              isExternalLink={irInvestorLink.isExternalLink}
            />
          </LinkCardList>
          <Spacer h={{ sp: 70, pc: 97 }} />
          <TwoColumns>
            <LeftColumn>
              <section>
                <SectionButtonTitle>
                  <SectionTitle>最新IR資料</SectionTitle>
                  <DownloadZipButton id="irp-library-latest-zip" />
                </SectionButtonTitle>
                <DocumentList id="irp-library-quarter-list" /> 
              {/* id="irp-library-term-list"のdiv要素がないと
                id="irp-library-quarter-list"のためのjsがロードされない仕様とのこと */}
              <Onlyload id="irp-library-term-list" />
              </section>
              <Spacer h={{ sp: 70, pc: 80 }} />
              <section>
                <SectionButtonTitle>
                  <SectionTitle>IRニュース</SectionTitle>
                  <Link href={irNewsLink} passHref>
                    <a>
                      <ShowAllButton btnStyle="transparent">
                        一覧を見る
                      </ShowAllButton>
                    </a>
                  </Link>
                </SectionButtonTitle>
                <RecentNewsList />
              </section>
            </LeftColumn>
            <RightColumn>
              <StockPriceInfoCard />
              <Spacer h={{ sp: 30, pc: 44 }} />
              <MonthlySalesCard {...monthlySales} />
              <Spacer h={{ sp: 30, pc: 44 }} />
              <CalenderCard items={irCalendar}/>
            </RightColumn>
          </TwoColumns>
        </Layout>
        <ThreeImg>
          <section>
            <SectionButtonTitle>
              <SectionTitle>社外評価</SectionTitle>
              <Link href="./ir/evaluation">
                <a>
                  <ShowAllButton btnStyle="transparent">
                    一覧を見る
                  </ShowAllButton>
                </a>
              </Link>
            </SectionButtonTitle>
          </section>
          <div style={{display: "flex", flexWrap: "wrap", flex: "1 1 33.33%", order: 3}}>
            <DivImg>
              <a href="https://jsite.mhlw.go.jp/fukuoka-roudoukyoku/content/contents/000393811.pdf" target="_blank" rel="noopener noreferrer" className="pdf_after">
                <img style={{height: "185px", margin: "0 auto"}} src="/images/ir-approval.png" alt="" />
              </a>
            </DivImg>
            <DivImg>
              <a href="https://jsite.mhlw.go.jp/fukuoka-roudoukyoku/content/contents/001294466.pdf" target="_blank" rel="noopener noreferrer" className="pdf_after">
                <img style={{height: "185px", margin: "0 auto"}} src="/images/ir-certification.jpg" alt="" />
              </a>
            </DivImg>
            <DivImg>
              <a href="https://pdf.irpocket.com/C141A/jQ9P/ZH6w/duVd.pdf" target="_blank" rel="noopener noreferrer" className="pdf_after">
                <img style={{height: "185px", margin: "0 auto"}} src="/images/ir-winning.png" alt="" />
              </a>
            </DivImg>
          </div>
        </ThreeImg>
      </ImageTitleLayout>
    </>
  )
}

const ThreeImg = styled.div`
  width: 1040px;
  margin: 0 auto 130px;
  @media screen and (max-width: 1047px) {
    width: calc(100% - 40px);margin: 0 auto;
  }
`

const DivImg = styled.div`
  width: 33.33%;
  text-align: center;
  min-width: 220px;
  margin-top: 50px;
  @media screen and (max-width: 1047px) {
    margin-bottom: 30px;
    margin-top: 15px;
  }
  @media screen and (max-width: 700px) {
    width: 100%;
  }
  &:hover {
    a {
      text-decoration: underline;
      color: ${theme.colors.hoverLightBlue};
    }

    .pdf_after:after {
      background: url('/images/external-link-hoverblue.svg') no-repeat;
    }
  }

  .pdf_after {
    &:after {
      content: '';
      background: url('/images/external-link-black.svg') no-repeat;
      width: 12px;
      height: 12px;
      margin-left: 8px;
      display: inline-block;
    }
  }
`

const TwoColumns = styled.div`
  display: flex;
  @media screen and (max-width: 1047px) {
    flex-direction: column;
    row-gap: 60px;
  }
`
const LeftColumn = styled.div`
  width: 745px;
  margin-right: 55px;
  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`
const SectionButtonTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 0px 60px;
  gap: 30px;

  @media screen and (max-width: 1047px) {
    padding: 0px 0px 30px;
    gap: 16px;
  }
`
const SectionTitle = styled.h2`
  font-weight: 700;
  font-size: 32px;
  color: ${theme.colors.trialBlue};

  @media screen and (max-width: 1047px) {
    padding: 0px;
    font-weight: 700;
    font-size: 24px;
    line-height: 130%;
  }
`

const DocumentList = styled.ul`
  @media screen and (max-width: 1047px) {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  > li {
    display: flex;
    align-items: center;
    width: 100%;
    :not(:last-child) {
      border-bottom: 1px solid ${theme.colors.backgroundBlue};
    }
    @media screen and (max-width: 1047px) {
      flex-direction: column;
      padding-top: 0px;
      padding-bottom: 14px;
      gap: 2px;
      align-items: flex-start;
    }

    > a {
      transition: color 0.3s ease;
      display: flex;
      gap: 50px;
      width: 100%;
      padding-top: 18px;
      padding-bottom: 20px;
      @media screen and (max-width: 1047px) {
        flex-direction: column;
        row-gap: 2px;
      }

      &:hover > h3 {
        > svg {
          fill: ${theme.colors.hoverLightBlue};
        }
        > p {
          text-decoration: underline;
          color: ${theme.colors.hoverLightBlue};
          > span {
            color: ${theme.colors.hoverLightBlue};
            display: inline-block;
          }
        }
      }

      > time {
        font-weight: 700;
        font-size: 14px;
        line-height: 1.6;
      }

      > h3 {
        display: flex;
        gap: 10px;

        @media screen and (max-width: 1047px) {
          font-size: 15px;
          line-height: 24px;
          letter-spacing: 0;
        }

        > svg {
          fill: ${theme.colors.trialBlue};
          width: 12px;
          height: 28px;
          flex-shrink: 0;
        }

        > p {
          font-size: 16px;
          letter-spacing: 0.64px;
          line-height: 26px;
          color: ${theme.colors.textTitle};
          @media screen and (max-width: 1047px) {
            font-size: 15px;
          }

          > span {
            font-weight: 500;
            font-size: 13px;
            line-height: 1.6;
            color: ${theme.colors.textSecond};
            margin-left: 10px;
            @media screen and (max-width: 1047px) {
              font-size: 11px;
            }
          }
        }
      }
    }
  }
`

const Onlyload = styled.div`
  display: none;
`
const ShowAllButton = styled(Button)`
  padding: 11px 26px 13px 26px;
  width: 135px;
  height: 40px;
  font-size: 16px;
  font-weight: 500;

  @media screen and (max-width: 1047px) {
    padding: 2px 18px 4px 18px;
    font-size: 13px;
    width: 104px;
    height: 32px;
  }
`

const RightColumn = styled.div`
  width: 240px;
  @media screen and (max-width: 1047px) {
    width: 335px;
    align-items: center;
    margin: 0 auto;
  }
`
// 第１期四半期決算説明会を非表示
// const HiddenCmsLinkCard = styled.div`
//   display: none;
// `
